import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import _ from 'lodash';
import moment from 'moment-timezone';

import { PageHeader } from '../../../../components/PageHeader';
import { TextInput } from '../../../../components/PageHeader/TextInput';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import Table, { Column } from '../../../../containers/Table/TableWithPagination';
import Strings from '../../../../Strings';
import { actions } from '../redux/actions';
import { openModalAction } from '../../../../actions/modal';
import { COMPLETE_MONITORING_MODAL_NAME } from '../../../../modals/CompleteMonitoringModal/CompleteMonitoringModal';
import { ItemSelectorHelper } from '../../../../components/ActionWidget';
import { SelectionBar, SelectionBarButton } from '../../../../components/SelectionBar';
import Select from '../../../../components/Select';
import AdvancedFilters, { Filter } from '../../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersDefinition } from '../../../../components/AdvancedFilters/FiltersDefinition';
import { getFiltersForRequest, urlInjected } from '../../../../components/AdvancedFilters/helpers';
import { downloadFileOnly, getVitalsData, getVitalsDate, getVitalsStatus } from '../../../../utils';
import { DateFilters } from '../../../../utils/DateFilters';
import OrgSelect from '../../../../components/OrgSelect/OrgSelect';
import { getGlobalPageSize } from '../../../../utils/tableUtils';
import DropdownMenu from '../../../../components/DropdownMenu/DropdownMenu';

const defaultStatus = [
  {
    label: Strings.readingStatus.allReadings,
    value: '',
  },
  {
    label: Strings.readingStatus.newReadings,
    value: `newreadings`,
  },
  {
    label: Strings.readingStatus.archivedReadings,
    value: `archivedreadings`,
  },
];

// newest readings first; the backend uses the same order when no sort is requested
const DEFAULT_ORDER = { key: 'date', sortKey: 'date', direction: 'descending' };

// the shared 6/12 months ranges end with the last full month, which would hide today's readings here
const untilNow = filter => ({ ...filter, dates: { ...filter.dates, endDate: () => moment().format() } });

const DATE_RANGES = [
  DateFilters.AllTime,
  DateFilters.Last48Hours,
  DateFilters.ThisMonth,
  DateFilters.Last30Days,
  DateFilters.LastMonth,
  untilNow(DateFilters.Last6Months),
  DateFilters.ThisYear,
  untilNow(DateFilters.Last12Months),
];

export class Readings extends PureComponent {
  searchTime;
  refreshEnable = true;
  hasFiltersInUrl = urlInjected(window.location.search);
  hasSeenHydratedFilters = false;

  state = {
    checkedItems: {},
    statusFilter: 'newreadings',
    selectedDateFilter: DateFilters.Last48Hours.value,
  };

  itemSelectorHelper = new ItemSelectorHelper();

  // dates are computed from it on every load, so rolling ranges include readings that arrived after it was picked
  selectedDateRange = DateFilters.Last48Hours;

  pageRequest = {
    offset: 0,
    search: '',
    filterBy: 'Archived:false',
    sortColumn: DEFAULT_ORDER.sortKey,
    sortType: DEFAULT_ORDER.direction,
    limit: this.props.globalPageSize,
    orgId: this.props.orgIds && this.props.orgIds.length > 0 ? this.props.orgIds : undefined,
  };

  componentDidMount() {
    if (!urlInjected(window.location.search)) {
      this.load();
    }
  }
  componentDidUpdate(prevProps) {
    if (prevProps.readings !== this.props.readings) {
      this.keepOnlyVisibleSelection();
    }

    if (prevProps.refreshTimestamp !== this.props.refreshTimestamp || prevProps.orgIds !== this.props.orgIds) {
      this.pageRequest.orgId = (this.props.orgIds && this.props.orgIds.length > 0) ? this.props.orgIds : undefined;
      this.load();
    }

    const { filters } = this.props;
    if (filters !== prevProps.filters) {
      if (this.hasFiltersInUrl && !this.hasSeenHydratedFilters && filters.length === 0) {
        return;
      }
      if (filters.length > 0) {
        this.hasSeenHydratedFilters = true;
      }
      this.onFiltersChange(filters);
    }
  }

  load() {
    this.pageRequest.startDate = this.selectedDateRange.dates.startDate();
    this.pageRequest.endDate = this.selectedDateRange.dates.endDate();
    this.props.getReadings(this.pageRequest);
  }

  onSearchQueryChange = query => {
    this.pageRequest.offset = 0;
    this.pageRequest.search = query;
    clearTimeout(this.searchTime);
    this.searchTime = setTimeout(() => {
      this.load();
    }, 1000);
  };

  onFiltersChange = filters => {
    return this.filtersDebounced(filters);
  };

  filtersDebounced = _.debounce(() => {
    let newFiltersForRequest = getFiltersForRequest(this.props.filters);
    if (JSON.stringify(newFiltersForRequest) !== JSON.stringify(this.pageRequest.filterBy)) {
      newFiltersForRequest = newFiltersForRequest.map(f => f.replace('ReadingStatus', 'Status'));

      if (this.state.statusFilter === 'newreadings') {
        newFiltersForRequest.push('Archived:false');
      } else if (this.state.statusFilter === 'archivedreadings') {
        newFiltersForRequest.push('Archived:true');
      }

      this.pageRequest.filterBy = newFiltersForRequest;
      this.pageRequest.offset = 0;
    }
    this.load();
  }, 1000);

  // monitoring is reviewed per patient: selecting a row selects the whole kit
  getItemKey = item => item.kitId;

  isItemSelected = item => !!this.state.checkedItems[this.getItemKey(item)]?.checked;

  // selection is limited to the rows currently shown, so drop whatever is no longer on the page
  keepOnlyVisibleSelection = () => {
    this.setState(prevState => {
      const visibleKeys = new Set((this.props.readings || []).map(item => String(this.getItemKey(item))));
      const checkedItems = _.pickBy(prevState.checkedItems, (value, key) => visibleKeys.has(key));
      return _.size(checkedItems) === _.size(prevState.checkedItems) ? null : { checkedItems };
    });
  };

  onSelectAllChanged = checked => {
    const { readings } = this.props;
    const pageItems = checked
      ? Object.fromEntries((readings || []).map(item => [this.getItemKey(item), { checked, item }]))
      : {};
    this.setState({ checkedItems: pageItems });
  };

  getSelection = () => {
    const { readings } = this.props;
    const { statusFilter } = this.state;
    const selectedCount = (readings || []).filter(this.isItemSelected).length;
    return {
      isSelected: this.isItemSelected,
      onToggle: this.itemChecked,
      allSelected: !!readings?.length && selectedCount === readings.length,
      someSelected: selectedCount > 0,
      onToggleAll: this.onSelectAllChanged,
      // In "All readings" the page mixes new and archived rows, so archiving all of them has no single meaning
      disabled: !readings?.length || !statusFilter,
      disabledTooltip: !statusFilter ? Strings.selectAllUnavailableForAllReadings : undefined,
    };
  };

  getSelectedItems = () => Object.values(this.state.checkedItems).filter(e => e.checked);

  getSelectionBarTitle = selectedItems => {
    const count = selectedItems.length;
    return count === 1 ? Strings.onePatientSelected : Strings.formatString(Strings.xPatientsSelected, count);
  };

  // mirrors the direction onArchive takes for the current status filter
  getArchiveLabel = () => {
    const { statusFilter } = this.state;
    if (statusFilter === 'newreadings') {
      return Strings.completeMonitoring;
    }
    if (statusFilter === 'archivedreadings') {
      return Strings.messages.unarchive;
    }
    return Strings.archiveUnarchive;
  };

  getHeaderComponents = () => {
    return (
      <>
        <TextInput class="" placeholder={Strings.search} onChange={e => this.onSearchQueryChange(e.target.value)} />
        <Select data={defaultStatus} onChange={this.onStatusChanged} value={this.state.statusFilter} />
        <Select
          data={DATE_RANGES}
          onChange={this.onDateRangeChanged}
          value={this.state.selectedDateFilter}
        />
        <AdvancedFilters lastButton>
          <Filter definition={getFiltersDefinition().DeviceType} />
          <Filter definition={getFiltersDefinition().ReadingStatus} />
        </AdvancedFilters>
      </>
    );
  };

  itemChecked = (item, checked) => {
    this.setState(prevState => ({
      checkedItems: { ...prevState.checkedItems, [this.getItemKey(item)]: { checked, item } },
    }));
  };

  // the table passes the new limit when the page size is changed
  onOffsetChange = (offset, limit) => {
    this.pageRequest.offset = offset;
    if (limit) {
      this.pageRequest.limit = limit;
    }
    this.load();
  };

  onSortClick = ({ sortKey, direction }) => {
    // the table also reports its initial (default) order, which is already loaded
    if (this.pageRequest.sortColumn === sortKey && this.pageRequest.sortType === direction) {
      return;
    }
    this.pageRequest.offset = 0;
    this.pageRequest.sortColumn = sortKey;
    this.pageRequest.sortType = direction;
    this.load();
  };

  onRefresh = () => {
    this.refreshEnable = false;
    this.turnOffTimeout = setTimeout(() => {
      this.refreshEnable = true;
      this.forceUpdate();
    }, 10000);
    this.load();
  };

  resetActionWidget = () => {
    this.setState({
      checkedItems: {},
    });
  };

  onDateRangeChanged = option => {
    this.setState({ selectedDateFilter: option.value });
    this.selectedDateRange = option;
    this.pageRequest.offset = 0;

    this.load();
  };

  onStatusChanged = option => {
    const newFiltersForRequest = getFiltersForRequest(this.props.filters);
    this.setState({ statusFilter: option.value });
    // selection made under another status would be archived/unarchived the wrong way
    this.resetActionWidget();
    if (option.value === 'newreadings') {
      newFiltersForRequest.push('Archived:false');
    } else if (option.value === 'archivedreadings') {
      newFiltersForRequest.push('Archived:true');
    }
    this.pageRequest.filterBy = newFiltersForRequest;
    this.pageRequest.offset = 0;
    this.load();
  };

  // completing monitoring asks for the time spent on the review first; unarchiving needs no time
  onSelectionAction = () => {
    if (this.state.statusFilter !== 'newreadings') {
      this.onArchive();
      return;
    }
    this.props.openCompleteMonitoring({
      patientsCount: this.getSelectedItems().length,
      onConfirm: durationSeconds => this.onArchive(durationSeconds),
    });
  };

  // durationSeconds is the time logged per review; without it the backend logs its default
  onArchive = durationSeconds => {
    const { checkedItems, statusFilter } = this.state;
    const { toggleArchieved } = this.props;

    const requests = Object.values(checkedItems)
      .filter(e => e.checked)
      .map(e => {
        // a filtered view has a single direction; "All readings" keeps per-row toggling
        const archived =
          statusFilter === 'newreadings' ? true : statusFilter === 'archivedreadings' ? false : !e.item.archived;
        // no deviceId: the backend reviews every device in the kit
        const data = { kitId: e.item.kitId, archived, durationSeconds };
        return toggleArchieved(data);
      });
    this.resetActionWidget();
    return Promise.all(requests).then(() => this.load());
  };

  onRowSelection = id => {
    const { readings } = this.props;

    // check if id is in range of readings table
    if (id < 0 || id >= readings.length) {
      return;
    }

    const patientId = readings[id].patientId;
    this.props.onNavigate(`/cap-patients/${patientId}/dashboard`);
  };

  onDownloadBtnClickSuccessHandler = (req, filename) => {
    const fileName =
      req?.response && req?.response?.name ? req?.response?.name : filename ? filename : moment().toString();
    downloadFileOnly(req, fileName);
  };

  onDownloadMonitoringReport = () => {
    this.props.downloadMonitoringReport({ timezone: this.props.timezone })
      .then(resp =>
        this.onDownloadBtnClickSuccessHandler(resp, `MonitoringReport_${moment().format('YYYYMMDD_HHmmss')}.csv`),
      );
  };

  render() {
    const { isLoading, readings, pagination, orgs } = this.props;
    const selectedItems = this.getSelectedItems();

    let columns = [
      <Column
        key="patientName"
        sortKey="patientName"
        title={Strings.capPatient.patientName}
        className="clickable"
        value={e => e.patientName}
      />,
      <Column key="pharmacyPatientId" title={Strings.capPatient.patientId} value={e => e.pharmacyPatientId} />,
    ];

    if (orgs?.length > 1) {
      columns.push(
        <Column key="orgName" sortKey="orgName" title={Strings.organization} value={e => e.organization} />
      );
    }
    columns = columns.concat([
      <Column key="deviceType" sortKey="deviceType" title={Strings.capPatient.deviceType} value={e => e.deviceType} />,
      <Column key="deviceId" sortKey="deviceId" title={Strings.capPatient.deviceId} value={e => e.deviceId} />,
      <Column
        key="status"
        sortKey="status"
        title={Strings.capPatient.status}
        value={e => (!_.isEmpty(e.vitals) ? getVitalsStatus(e.vitals) : Strings.normal)}
      />,
      <Column
        key="date"
        title={Strings.capPatient.date}
        sortKey="date"
        value={e => (!_.isEmpty(e.vitals) ? moment(getVitalsDate(e.vitals)).format(DATE_TIME_SPACE_12_UPPERCASE) : '-')}
      />,
      <Column
        key="vitals"
        title={Strings.value}
        value={e => (!_.isEmpty(e.vitals) ? getVitalsData(e.vitals) : '-')}
      />,
      <Column key="deviceName" sortKey="deviceName" title={Strings.capPatient.deviceName} value={e => e.deviceName} />,
    ]);

    const headerTitle =
      !isLoading && readings && pagination
        ? Strings.formatString(
            Strings.showingXReadings,
            readings.length,
            pagination.totalRecords ? pagination.totalRecords : 0,
          )
        : Strings.showingWait;

    return (
      <React.Fragment>
        <PageHeader
          right={(
            <>
              <OrgSelect />
              <DropdownMenu
                title={Strings.actions}
                options={[
                  { onSelect: this.onDownloadMonitoringReport, label: Strings.downloadMonitoringReport },
                ]}
              />
            </>
          )}
          left={Strings.monitoring}
          noLeftPadding
        />
        <PageHeader right={() => this.getHeaderComponents()} left={headerTitle} noLeftPadding />
        <AdvancedFiltersBar />
        <SelectionBar
          show={selectedItems.length > 0}
          title={this.getSelectionBarTitle(selectedItems)}
          onClear={this.resetActionWidget}
        >
          <SelectionBarButton
            primary
            icon={this.state.statusFilter === 'newreadings' ? 'check' : 'archive'}
            onClick={this.onSelectionAction}
            testId="selection-bar-archive"
          >
            {this.getArchiveLabel()}
          </SelectionBarButton>
        </SelectionBar>
        <Table
          isLoading={isLoading}
          name="patient"
          uuid="347938a5-c9a1-4151-b717-f93b3e5f7a87"
          enableColumnFiltering
          data={readings || []}
          onRowSelection={this.onRowSelection}
          onOffsetChange={this.onOffsetChange}
          onSortClick={this.onSortClick}
          pagination={pagination}
          defaultOrder={DEFAULT_ORDER}
          selection={this.getSelection()}
        >
          {columns}
        </Table>
      </React.Fragment>
    );
  }
}

Readings.propTypes = {
  getReadings: PropTypes.func,
  isLoading: PropTypes.any,
  onArchive: PropTypes.func,
  onNavigate: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
  }),
  readings: PropTypes.array,
  refreshTimestamp: PropTypes.any,
  filters: PropTypes.array,
  toggleArchieved: PropTypes.func,
  orgIds: PropTypes.string,
  orgs: PropTypes.array,
  globalPageSize: PropTypes.number,
  timezone: PropTypes.string,
  downloadMonitoringReport: PropTypes.func,
  openCompleteMonitoring: PropTypes.func,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    readings: patients && patients?.readings,
    isLoading: patients && patients?.isLoading,
    pagination: patients && patients?.pagination,
    filters: state.entities.advancedFilters.filters.items,
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
    globalPageSize: getGlobalPageSize(state),
    timeozone: state.entities?.user?.timezone,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getReadings: pageRequest => dispatch(actions.getReadings(pageRequest)),
  toggleArchieved: data => dispatch(actions.archiveToggle(data)),
  getCareProvidedOrganizations: () => dispatch(actions.getCareProvidedOrganizations()),
  downloadMonitoringReport: (params) => dispatch(actions.downloadPatientsMonitoringReport(params)),
  openCompleteMonitoring: data => dispatch(openModalAction(COMPLETE_MONITORING_MODAL_NAME, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Readings);
